import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';
import chestXrayImage from './assets/chest-xray.png';
import logoImage from './assets/Nigeria.png';

/* ─────────────── DATA ─────────────── */
const PIPELINE = [
  {
    n: "01",
    title: "Upload",
    short: "Upload chest X-ray image to the platform",
    long: "",
    badges: ["DICOM", "PNG/JPG", "Secure Upload"],
    micro: [],
    overlay: "AWAITING IMAGE",
    grid: false,
    heat: false,
    scan: false,
    result: null,
  },
  {
    n: "02",
    title: "Analyze",
    short: "AI reviews the chest X-ray for signs of TB",
    long: "",
    badges: ["AI Model", "Image Analysis", "TB Detection"],
    micro: [],
    overlay: "ANALYZING IMAGE",
    grid: true,
    heat: false,
    scan: true,
    result: null,
  },
  {
    n: "03",
    title: "Explain",
    short: "Shows the image areas that influenced the result",
    long: "",
    badges: ["Heatmap", "Visual Explanation", "Clinical Support"],
    micro: [],
    overlay: "EXPLANATION READY",
    grid: false,
    heat: true,
    scan: false,
    result: {
      label: "Prediction",
      value: "TB +",
      cls: "bad",
      sub: "Confidence 92.4%",
    },
  },
];

const METRICS = [
  { label: "Validation Accuracy", value: 93, suffix: "%" },
  { label: "Precision",           value: 70, suffix: "%" },
  { label: "Recall",              value: 85, suffix: "%" },
  { label: "F1-Score",            value: 77, suffix: "%" },
];

const AUDIENCES = [
  { num: "01 · PRIMARY",   title: "Hospitals & Radiology Clinics",  items: ["Faster screening workflows", "AI-assisted radiology support", "Reduced manual workload", "Decision-support assistance"] },
  { num: "02 · PRIMARY",   title: "Diagnostic Centers",              items: ["Triage high-volume X-ray queues", "Confidence scoring on each scan", "Local deployment — no cloud uplink", "Auditable Grad-CAM evidence"] },
  { num: "03 · PRIMARY",   title: "Public Health Organizations",     items: ["Improved accessibility to screening", "Scalable deployment opportunities", "Support for high-burden regions", "Standardized reporting"] },
  { num: "04 · SECONDARY", title: "Researchers",                     items: ["Open Grad-CAM outputs", "Model card transparency", "Reproducible inference pipeline", "Dataset & metrics published"] },
];

const EXPL = [
  { k: "01", h: "Greater trust",    p: "Clinicians see exactly which lung regions drove the prediction — no black box." },
  { k: "02", h: "Interpretability", p: "Per-pixel activation gradients translate raw CNN outputs into a clinical visual." },
  { k: "03", h: "Better review",    p: "Heatmaps surface in the same UI as the prediction, supporting fast triage decisions." },
  { k: "04", h: "Auditability",     p: "Every prediction can be exported as a PDF including the image, region, and confidence." },
];

const FAQ = [
  {
    q: "Is TBDetect AI intended to replace radiologists?",
    a: "No. TBDetect AI is designed as a clinical decision-support tool that assists radiologists and healthcare professionals during chest X-ray interpretation. Final clinical decisions always remain with qualified healthcare practitioners.",
  },
  {
    q: "What makes TBDetect AI different from other AI models?",
    a: "Many AI systems function as black boxes, making it difficult to understand how predictions are generated. TBDetect AI incorporates explainable AI techniques that provide visual heatmaps, helping clinicians interpret and validate AI-assisted findings.",
  },
  {
    q: "Can the system explain why it made a prediction?",
    a: "Yes. TBDetect AI uses Grad-CAM visualization to highlight the areas of the chest X-ray that contributed most to the prediction, improving transparency and clinical trust.",
  },
  {
    q: "How long does it take to analyze an X-ray?",
    a: "Analysis is typically completed within seconds after image upload, depending on the hardware and deployment environment.",
  },
  {
    q: "How accurate is the system?",
    a: "The model achieved approximately 94% validation accuracy during experimental evaluation on public tuberculosis chest X-ray datasets.",
  },
  {
    q: "Can clinicians review the original image alongside the AI results?",
    a: "Yes. The platform presents both the uploaded chest X-ray and the generated heatmap visualization, enabling clinicians to compare the original image with the AI interpretation.",
  },
];

function SmartQuestions() {
  const [open, setOpen] = useState(null);
  return (
    <div className="lp-faq-list">
      {FAQ.map((item, i) => {
        const isOpen = open === i;
        return (
          <div key={item.q} className={`lp-faq-item ${isOpen ? "open" : ""}`}>
            <button
              type="button"
              className="lp-faq-row"
              onClick={() => setOpen(isOpen ? null : i)}
              aria-expanded={isOpen}
            >
              <span className="lp-faq-q">{item.q}</span>
              <span className="lp-faq-icon" aria-hidden="true">{isOpen ? "−" : "+"}</span>
            </button>
            {isOpen && <div className="lp-faq-a">{item.a}</div>}
          </div>
        );
      })}
    </div>
  );
}

function HeroVisual() {
  const [heatOn, setHeatOn] = useState(false);
  const [conf, setConf] = useState(0);
  const [inView, setInView] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { setInView(true); obs.disconnect(); }
      }),
      { threshold: 0.3 }
    );
    if (containerRef.current) obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return;
    const t1 = setTimeout(() => setHeatOn(true), 1600);
    const interval = setInterval(() => {
      setConf((c) => {
        const target = 92.4;
        if (c < target) return Math.min(target, c + 1.6);
        return target;
      });
    }, 60);
    return () => { clearTimeout(t1); clearInterval(interval); };
  }, [inView]);

  return (
    <div ref={containerRef} className={`lp-hero-visual ${inView ? "in-view" : ""}`}>
      <span className="lp-corner tl"></span>
      <span className="lp-corner tr"></span>
      <span className="lp-corner bl"></span>
      <span className="lp-corner br"></span>
      <div className="lp-hv-img-wrap">
        <img src={chestXrayImage} alt="Chest X-ray" className="lp-hv-xray" />
        <div className={`lp-hv-heatmap ${heatOn ? "on" : ""}`}></div>
      </div>
      <div className={`lp-hv-bracket-target ${heatOn ? "on" : ""}`}></div>
      <div className="lp-hv-scanline"></div>
      <div className="lp-hv-hud">
        <div className="lp-hv-hud-top">
          <span className="lp-hv-tag"><span className="lp-tag-dot"></span>Live inference</span>
          <span className="lp-hv-tag">DenseNet121 · v2.1</span>
        </div>
        <div className="lp-hv-hud-bot">
          <div className="lp-hv-readout">
            <div className="lp-hv-readout-row"><span>Class</span><strong className="alarm">TB +</strong></div>
            <div className="lp-hv-readout-row"><span>Conf</span><strong>{conf.toFixed(1)}%</strong></div>
            <div className="lp-hv-readout-row"><span>Region</span><strong>L. Up. Lobe</strong></div>
            <div className="lp-hv-readout-row"><span>Latency</span><strong className="ok">1.2s</strong></div>
          </div>
          <span className="lp-hv-tag" style={{ alignSelf: "flex-end" }}>Grad-CAM · ON</span>
        </div>
      </div>
    </div>
  );
}

function StoryLine() {
  return (
    <div className="lp-story">
      {PIPELINE.map((step, i) => (
        <div className={`lp-story-row${i % 2 === 1 ? ' lp-story-row-flipped' : ''}`} key={step.n}>
          <div className="lp-story-title-col">
            <div className="lp-story-num">Step {step.n}</div>
            <h3 className="lp-story-title">{step.title}</h3>
          </div>
          <div className="lp-story-spine-col">
            <div className="lp-story-line"></div>
            <div className="lp-story-dot"></div>
          </div>
          <div className="lp-story-content-col">
            <p className="lp-story-short">{step.short}</p>
            <div className="lp-story-visual">
              <img src={chestXrayImage} alt="" />
              {step.grid && <div className="lp-pvp-grid-overlay on"></div>}
              {step.heat && <div className="lp-pvp-heatmap on"></div>}
              {step.scan && <div className="lp-story-scan"></div>}
              {step.result && (
                <div className="lp-story-result">
                  <span className="lp-story-result-label">{step.result.label}</span>
                  <span className={`lp-story-result-val ${step.result.cls}`}>{step.result.value}</span>
                  <span className="lp-story-result-sub">{step.result.sub}</span>
                </div>
              )}
            </div>
            <p className="lp-story-long">{step.long}</p>
            <div className="lp-story-badges">
              {step.badges.map((b) => <span key={b} className="lp-story-badge">{b}</span>)}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function GradCamDemo() {
  const [pos, setPos] = useState({ x: 50, y: 45 });

  const handleMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setPos({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
  };

  const dx = pos.x - 38, dy = pos.y - 42;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const activation = Math.max(0, Math.min(100, 100 - dist * 2.4));

  return (
    <div>
      <div className="lp-gradcam-demo">
        <div className="lp-gd-panel" onMouseMove={handleMove}>
          <img src={chestXrayImage} alt="Original chest X-ray" />
          <span className="lp-panel-label">Original · Input</span>
          <div className="lp-crosshair-h" style={{ top: `${pos.y}%` }}></div>
          <div className="lp-crosshair-v" style={{ left: `${pos.x}%` }}></div>
          <div className="lp-cursor-ring" style={{ left: `${pos.x}%`, top: `${pos.y}%` }}></div>
          <span className="lp-cursor-tag" style={{ left: `calc(${pos.x}% + 70px)`, top: `calc(${pos.y}% - 6px)` }}>
            x: {pos.x.toFixed(0)}, y: {pos.y.toFixed(0)}
          </span>
        </div>
        <div className="lp-gd-panel" onMouseMove={handleMove}>
          <img src={chestXrayImage} alt="Grad-CAM heatmap overlay" />
          <div className="lp-heatmap"></div>
          <span className="lp-panel-label">Grad-CAM · Layer conv5_block16</span>
          <div className="lp-crosshair-h" style={{ top: `${pos.y}%` }}></div>
          <div className="lp-crosshair-v" style={{ left: `${pos.x}%` }}></div>
          <div className="lp-cursor-ring" style={{ left: `${pos.x}%`, top: `${pos.y}%` }}></div>
          <span className="lp-cursor-tag" style={{ left: `calc(${pos.x}% + 70px)`, top: `calc(${pos.y}% - 6px)` }}>
            activation: {activation.toFixed(0)}
          </span>
        </div>
      </div>
      <div className="lp-gd-legend">
        <div className="lp-gd-legend-scale">
          <span className="lp-lbl">Low</span>
          <div className="lp-gradient"></div>
          <span className="lp-lbl">High activation</span>
        </div>
        <div className="lp-gd-meta">
          <span>Region:</span> Left Upper Lobe&nbsp;&nbsp;·&nbsp;&nbsp;
          <span>Hot pixel %:</span> 12.4&nbsp;&nbsp;·&nbsp;&nbsp;
          <span>Layer:</span> conv5_block16_2_conv
        </div>
      </div>
    </div>
  );
}

function MetricsRow() {
  const [visible, setVisible] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setVisible(true)),
      { threshold: 0.2 }
    );
    if (containerRef.current) obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div className="lp-metrics-row" ref={containerRef}>
      {METRICS.map((m) => <Metric key={m.label} {...m} animate={visible} />)}
    </div>
  );
}

function Metric({ label, value, suffix, animate }) {
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!animate) return;
    let raf;
    const start = performance.now();
    const duration = 1400;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setN(value * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [animate, value]);

  return (
    <div className="lp-metric">
      <p>{label}</p>
      <h4>{n.toFixed(0)}<span className="lp-pct">{suffix}</span></h4>
      <div className="lp-bar"><div className="lp-fill" style={{ width: animate ? `${value}%` : 0 }} /></div>
    </div>
  );
}

function RocCurve() {
  const pts = [];
  for (let i = 0; i <= 50; i++) {
    const x = i / 50;
    const y = 1 - Math.pow(1 - x, 0.08);
    pts.push([x, y]);
  }
  const W = 200, H = 200, pad = 16;
  const sx = (x) => pad + x * (W - pad * 2);
  const sy = (y) => H - pad - y * (H - pad * 2);
  const path = pts.map((p, i) => `${i === 0 ? "M" : "L"}${sx(p[0]).toFixed(1)},${sy(p[1]).toFixed(1)}`).join(" ");
  const areaPath = path + ` L${sx(1)},${sy(0)} L${sx(0)},${sy(0)} Z`;
  const grid = [];
  for (let i = 0; i <= 4; i++) {
    const t = i / 4;
    grid.push(
      <line key={`gx${i}`} x1={sx(t)} x2={sx(t)} y1={sy(0)} y2={sy(1)} stroke="rgba(255,255,255,0.05)" strokeWidth="1" />,
      <line key={`gy${i}`} y1={sy(t)} y2={sy(t)} x1={sx(0)} x2={sx(1)} stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
    );
  }
  return (
    <svg className="lp-roc-svg" viewBox="0 0 200 200" preserveAspectRatio="xMidYMid meet">
      {grid}
      <line x1={sx(0)} y1={sy(0)} x2={sx(1)} y2={sy(0)} stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
      <line x1={sx(0)} y1={sy(0)} x2={sx(0)} y2={sy(1)} stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
      <line x1={sx(0)} y1={sy(0)} x2={sx(1)} y2={sy(1)} stroke="rgba(255,255,255,0.18)" strokeWidth="1" strokeDasharray="3 3" />
      <path d={areaPath} fill="rgba(74,222,128,0.08)" />
      <path d={path} fill="none" stroke="#4ade80" strokeWidth="1.5" />
      <text x={sx(0.5)} y={H - 2} fontFamily="JetBrains Mono, monospace" fontSize="6" fill="#666" letterSpacing="1" textAnchor="middle">FALSE POSITIVE RATE →</text>
      <text x={4} y={sy(0.5)} fontFamily="JetBrains Mono, monospace" fontSize="6" fill="#666" letterSpacing="1" textAnchor="middle" transform={`rotate(-90, 4, ${sy(0.5)})`}>TRUE POSITIVE RATE →</text>
      <circle cx={sx(0.05)} cy={sy(0.94)} r="3" fill="#4ade80" />
      <text x={sx(0.05) + 8} y={sy(0.94) + 2} fontFamily="JetBrains Mono, monospace" fontSize="7" fill="#4ade80" letterSpacing="0.5">OPERATING PT</text>
    </svg>
  );
}

export default function LandingPage() {
  return (
    <div className="lp-root">
      <div className="lp-bg-grid"></div>
      <div className="lp-app">

        {/* NAV */}
        <nav className="lp-navbar">
          <div className="lp-brand">
            <img src={logoImage} alt="INL Diagnostics AI logo" className="lp-brand-logo" />
            <h1>INL Diagnostics AI</h1>
          </div>
          <div className="lp-nav-links">
            <a href="#home">Home</a>
            <a href="#how">How it works</a>
            <a href="#intro">About</a>
            <a href="#contact">Contact</a>
          </div>
          <Link to="/demo" className="lp-nav-cta">Try it now</Link>
        </nav>

        {/* HERO */}
        <header className="lp-hero" id="home">
          <div className="lp-hero-headline">
            <h1>AI-powered tuberculosis detection</h1>
          </div>
          <div className="lp-hero-body">
            <div className="lp-hero-text">
              <p className="lp-lede">
                TBDetect uses deep learning to analyze chest X-ray images and identify patterns associated with pulmonary tuberculosis — paired with Grad-CAM heatmaps so clinicians can see exactly what the model saw.
              </p>
            </div>
          </div>
          <div className="lp-hero-visual-wrap">
            <HeroVisual />
          </div>
        </header>

        {/* TRUST STRIP */}
        <div className="lp-trust-strip">
          <div className="lp-trust-inner">
            <span className="lp-trust-label">Built for</span>
            <div className="lp-trust-items">
              <span>Hospitals</span>
              <span>· Diagnostic Centers</span>
              <span>· Radiology Clinics</span>
              <span>· Public Health Agencies</span>
              <span>· NGOs</span>
              <span>· Occupational Health</span>
            </div>
          </div>
        </div>

        {/* PROBLEM */}
        <section className="lp-section" id="problem">
          <div className="lp-section-head">
            <h2>The status quo is devastating</h2>
          </div>
          <div className="lp-problem-grid">
            <div style={{ padding: 0 }}>
              <div className="lp-problem-stats">
                <div className="lp-problem-stat">
                  <div className="lp-stat-top">
                    <div className="lp-stat-eyebrow">Diagnostic Overlap</div>
                    <div><span className="lp-big">1</span><span className="lp-unit"> in 4</span></div>
                  </div>
                  <p>TB cases present with imaging that mimics other lung diseases.<span className="lp-stat-sub">On a chest X-ray, TB can sometimes be visually indistinguishable from other similar diseases like pneumonia or lung cancer, making it difficult to differentiate TB from other pulmonary diseases</span></p>
                </div>
                <div className="lp-problem-stat">
                  <div className="lp-stat-top">
                    <div className="lp-stat-eyebrow">Tired Eyes Miss More</div>
                    <div><span className="lp-big">40</span><span className="lp-unit">%</span></div>
                  </div>
                  <p>More findings get missed once a clinician's shift passes 8 hours.<span className="lp-stat-sub">And it's not just fatigue: for the very same X-ray, accuracy can swing from 21% to 71% depending on which specialist reads it. Who's on shift shouldn't decide whether TB gets caught.</span></p>
                </div>
                <div className="lp-problem-stat">
                  <div className="lp-stat-top">
                    <div className="lp-stat-eyebrow">Too Few Specialists</div>
                    <div><span className="lp-big">{'<300'}</span></div>
                  </div>
                  <p>Radiologists for Nigeria's 200M+ people.<span className="lp-stat-sub">The places hit hardest by TB have the fewest experts to catch it. Most screenings end up being read by staff who weren't trained for the job.</span></p>
                </div>
                <div className="lp-problem-stat">
                  <div className="lp-stat-top">
                    <div className="lp-stat-eyebrow">No Trust in Black Boxes</div>
                    <div><span className="lp-big">82</span><span className="lp-unit">%</span></div>
                  </div>
                  <p>Of clinicians won't adopt AI that can't explain its answers.<span className="lp-stat-sub">A diagnosis with no reasoning is hard to act on. If doctors can't see how the model decided, they won't use it.</span></p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* INTRODUCING */}
        <section className="lp-section lp-intro-section" id="intro">
          <h2 className="lp-intro-headline">
            <span>Introducing</span>
            <span>TBDetect AI</span>
          </h2>
          <p className="lp-intro-lede">
            Our explainable, AI-powered chest X-ray analysis tool enables the detection of tuberculosis with far greater accuracy and consistency than the status quo, it also shows clinicians exactly which regions of the lung drove the prediction.
          </p>
          <div className="lp-intro-stats">
            <div className="lp-intro-stat">
              <div className="lp-intro-stat-top">
                <span className="lp-intro-big">~94</span><span className="lp-intro-unit">%</span>
              </div>
              <p>
                <span className="lp-intro-headline-small">Model Accuracy</span>
                TBDetect AI demonstrated strong performance in detecting TB-related patterns from chest X-ray images.
              </p>
            </div>
            <div className="lp-intro-stat">
              <div className="lp-intro-stat-top">
                <span className="lp-intro-big-text">Explainable AI</span>
              </div>
              <p>
                <span className="lp-intro-headline-small">Interpretation & Trust</span>
                TBDetect AI reduces variability in chest X-ray interpretation by providing reliable analysis with transparent, Grad-CAM-based explainability.
              </p>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="lp-section lp-how-section" id="how">
          <h2 className="lp-how-headline">How it works</h2>
          <p className="lp-how-lede">Our fully-portable solution can be used anywhere.</p>
          <p className="lp-how-lede">The simple screening process takes less than 30 seconds per patient.</p>
          <StoryLine />
        </section>

        {/* EXPLAINABILITY */}
        <section className="lp-section" id="explainability">
          <div className="lp-section-head">
            <div className="lp-eyebrow"><span className="lp-dot"></span>Explainable AI</div>
            <h2>Grad-CAM heatmaps make every prediction reviewable.</h2>
            <p>We integrate gradient-weighted class activation mapping on the final convolutional layer of DenseNet121. The result: a per-pixel attribution map showing exactly which regions of the X-ray pushed the model toward TB-positive. Move your cursor over either panel to inspect.</p>
          </div>
          <GradCamDemo />
          <div className="lp-expl-grid">
            {EXPL.map((e) => (
              <div className="lp-expl-cell" key={e.k}>
                <div className="lp-icon-box">{e.k}</div>
                <h5>{e.h}</h5>
                <p>{e.p}</p>
              </div>
            ))}
          </div>
        </section>

        {/* PERFORMANCE */}
        <section className="lp-section lp-science-section" id="performance">
          <h2 className="lp-science-headline">The Science</h2>
          <p className="lp-science-lede">
            Our AI model analyzes chest X-rays to identify patterns uniquely associated with tuberculosis. Built on a fine-tuned DenseNet121 backbone and validated on over 11,000 CXR images, it surpasses standard symptomatic screening with 93% validation accuracy and a 0.96 ROC-AUC.
          </p>
          <MetricsRow />
          <div className="lp-roc-card">
            <RocCurve />
            <div className="lp-roc-text">
              <div className="lp-eyebrow" style={{ marginBottom: 14 }}>Receiver Operating Characteristic</div>
              <div className="lp-auc-big">0.96<small>ROC-AUC</small></div>
              <h3 style={{ marginTop: 28 }}>Near-perfect separation in held-out validation.</h3>
              <p>The curve sits firmly in the upper-left corner — a model that distinguishes TB-positive from TB-negative scans cleanly. Real-world deployment performance will depend on data distribution and clinical workflow.</p>
            </div>
          </div>
        </section>

        {/* AUDIENCES */}
        <section className="lp-section lp-audience-section" id="audience">
          <h2 className="lp-aud-headline">Who it's for?</h2>
          <div className="lp-aud-matrix">
            {AUDIENCES.map((a) => (
              <div className={`lp-aud-cell ${a.num.includes('PRIMARY') ? 'lp-aud-primary' : 'lp-aud-secondary'}`} key={a.title}>
                <div className="lp-aud-num">{a.num}</div>
                <h5>{a.title}</h5>
                <ul>
                  {a.items.map((item) => <li key={item}>{item}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* DISCLAIMER */}
        <section className="lp-section" style={{ paddingTop: 0 }}>
          <div className="lp-disclaimer-card">
            <div className="lp-warn-icon">!</div>
            <div>
              <h3>Medical disclaimer</h3>
              <p>
                TBDetect is designed as an AI-assisted clinical decision-support tool and is{' '}
                <strong style={{ color: "var(--lp-text)" }}>not intended to replace professional medical diagnosis or clinical judgment</strong>.
                The platform is currently an experimental research and development system undergoing ongoing evaluation and improvement. It is not a substitute for qualified healthcare professionals.
              </p>
            </div>
          </div>
        </section>

        {/* SMART QUESTIONS */}
        <section className="lp-section lp-faq-section" id="faq">
          <h2 className="lp-faq-headline">Smart questions</h2>
          <SmartQuestions />
        </section>

        {/* CTA BAND */}
        <section className="lp-cta-band" id="cta">
          <div className="lp-cta-inner">
            <div>
              <h3>See TBDetect on your own chest X-rays.</h3>
              <p>Try it now or grab the research documentation — we'll get back within a few working days.</p>
            </div>
            <div className="lp-actions">
              <Link to="/demo" className="lp-btn-primary">Try it now <span className="lp-arrow">→</span></Link>
              <a href="#roadmap" className="lp-btn-secondary">Read the roadmap</a>
            </div>
          </div>
        </section>

        {/* FOOTER */}
        <footer className="lp-footer" id="contact">
          <div className="lp-footer-grid">
            <div className="lp-footer-brand">
              <div className="lp-brand">
                <img src={logoImage} alt="INL Diagnostics AI logo" className="lp-brand-logo" />
                <h1>INL Diagnostics AI</h1>
              </div>
              <p>AI-assisted chest X-ray analysis for early tuberculosis screening. A research and development platform built around explainability and clinical workflow.</p>
            </div>
            <div>
              <h4>Product</h4>
              <ul>
                <li><a href="#how">How it works</a></li>
                <li><a href="#explainability">Explainable AI</a></li>
                <li><a href="#tech">Technology</a></li>
                <li><a href="#performance">Performance</a></li>
              </ul>
            </div>
            <div>
              <h4>Research</h4>
              <ul>
                <li><a href="#roadmap">Roadmap</a></li>
                <li><a href="#roadmap">Model card</a></li>
                <li><a href="#roadmap">Datasets</a></li>
                <li><a href="#roadmap">Publications</a></li>
              </ul>
            </div>
            <div>
              <h4>Contact</h4>
              <ul>
                <li><Link to="/demo">Try it now</Link></li>
                <li><a href="#contact">Partnerships</a></li>
                <li><a href="#contact">Press</a></li>

              </ul>
            </div>
          </div>
          <div className="lp-footer-bottom">
            <span>© 2026 TBDetect AI · Experimental R&amp;D Platform</span>

          </div>
        </footer>

      </div>
    </div>
  );
}
